import { OidcClient } from '@axa-fr/react-oidc';
import { getLayoutView as getLayoutViewFromLib } from '@healthhub/message-channel';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { matchRoutes, useLocation } from 'react-router';

import { LayoutType, SourceApp, SourceAppView } from '@shared/constants/constants';
import type { SourceAppViewType } from '@shared/constants/constants';
import type { RouteConfig } from '@shared/types/common.types';
import { flattenRoute } from '@shared/utils/routes';

import { LayoutViewBridge, LayoutViewContext } from './LayoutViewContext';

interface LayoutViewContextProviderProps {
  ROUTES: RouteConfig[];
}

const LayoutViewContextProvider = ({ children, ROUTES }: PropsWithChildren<LayoutViewContextProviderProps>) => {
  const location = useLocation();

  const value = useMemo(() => {
    const flattenedRoutes = flattenRoute(ROUTES);
    const matchedPath = (matchRoutes(flattenedRoutes, location)?.[0]?.route ?? {}) as RouteConfig;
    const { availableViews = [] } = matchedPath;

    const oidc = OidcClient.get();
    const {
      isMobileView: isMobileViewFromLib,
      sourceApp,
      capabilityVersion,
    } = getLayoutViewFromLib({ token: oidc.tokens });

    const isStandardView = !!(sourceApp && sourceApp !== SourceApp.HH && availableViews.includes(LayoutType.STANDARD));
    const isSharedServiceView = !!(
      sourceApp &&
      sourceApp !== SourceApp.HH &&
      availableViews.includes(LayoutType.SHARED)
    );
    const isCommonView = availableViews.includes(LayoutType.COMMON);
    const isMobileView = isMobileViewFromLib && !isCommonView && availableViews.includes(LayoutType.MOBILE);
    const isPortalView = !isStandardView && !isMobileView && !isSharedServiceView && !isCommonView;

    const source = sourceApp || SourceApp.HH;
    let sourceAppView: SourceAppViewType;
    if (source === SourceApp.HH) {
      sourceAppView = isPortalView ? SourceAppView.HH_WEB : SourceAppView.HH_MOBILE;
    } else {
      sourceAppView = SourceAppView[sourceApp];
    }

    const result = {
      capabilityVersion,
      sourceAppView,
      isCommonView,
      isMobileView,
      isPortalView,
      isSharedServiceView,
      isStandardView,
      sourceApp: source,
    };
    LayoutViewBridge.setGetLayoutViewBridge(() => result);

    return result;
  }, [location, ROUTES]);

  return <LayoutViewContext.Provider value={value}>{children}</LayoutViewContext.Provider>;
};

export default LayoutViewContextProvider;
